/**
 * Dates as the product reads them: local, ISO in the URL, short English on screen.
 *
 * Two mistakes kept recurring. `new Date().toISOString().slice(0, 10)` is UTC, so from
 * 18:30 IST the calendar ringed tomorrow as today. And an ISO date leaked straight into
 * a filter pill — "2026-08-01 → 2026-08-31" — next to dates the same screen formats.
 * One module, so the register, the calendar and the dashboard cannot disagree.
 */

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const pad = (n) => String(n).padStart(2, "0");

/** A Date → YYYY-MM-DD in the browser's own zone, never UTC. */
export function isoDate(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** YYYY-MM-DD → a local Date at midnight. null for anything else. */
export function parseIso(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ""));
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

export function localToday() {
  return isoDate(new Date());
}

/** "26 Aug 2026". Hands back whatever it was given when it is not a date. */
export function fmtDate(iso) {
  const d = parseIso(iso);
  if (!d) return iso ? String(iso) : "";
  return `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}`;
}

/** "1 Aug – 31 Aug 2026", "from 1 Aug 2026", "until 31 Aug 2026". */
export function rangeLabel({ from, to }) {
  if (from && to) {
    const a = parseIso(from), b = parseIso(to);
    if (a && b && a.getFullYear() === b.getFullYear()) {
      return `${a.getDate()} ${MONTHS_SHORT[a.getMonth()]} – ${fmtDate(to)}`;
    }
    return `${fmtDate(from)} – ${fmtDate(to)}`;
  }
  if (from) return `from ${fmtDate(from)}`;
  if (to) return `until ${fmtDate(to)}`;
  return "";
}

const monthStart = (y, m) => new Date(y, m, 1);
const monthEnd = (y, m) => new Date(y, m + 1, 0);

/**
 * The periods somebody asks a training register about. Indian financial year runs
 * April to March, which is the one an auditor at Shakun means by "this year".
 * Each answers { from, to } for the day it is asked on.
 */
export const DATE_PRESETS = [
  { key: "this_month", label: "This month", range: (t) => [monthStart(t.getFullYear(), t.getMonth()), monthEnd(t.getFullYear(), t.getMonth())] },
  { key: "last_month", label: "Last month", range: (t) => [monthStart(t.getFullYear(), t.getMonth() - 1), monthEnd(t.getFullYear(), t.getMonth() - 1)] },
  { key: "last_30", label: "Last 30 days", range: (t) => [new Date(t.getFullYear(), t.getMonth(), t.getDate() - 29), t] },
  { key: "this_quarter", label: "This quarter", range: (t) => {
    const q = Math.floor(t.getMonth() / 3) * 3;
    return [monthStart(t.getFullYear(), q), monthEnd(t.getFullYear(), q + 2)];
  } },
  { key: "this_fy", label: "This financial year", range: (t) => {
    const startYear = t.getMonth() >= 3 ? t.getFullYear() : t.getFullYear() - 1;
    return [new Date(startYear, 3, 1), new Date(startYear + 1, 2, 31)];
  } },
  { key: "last_fy", label: "Last financial year", range: (t) => {
    const startYear = (t.getMonth() >= 3 ? t.getFullYear() : t.getFullYear() - 1) - 1;
    return [new Date(startYear, 3, 1), new Date(startYear + 1, 2, 31)];
  } },
  { key: "this_year", label: "This calendar year", range: (t) => [new Date(t.getFullYear(), 0, 1), new Date(t.getFullYear(), 11, 31)] },
];

export function presetRange(key, today = new Date()) {
  const p = DATE_PRESETS.find((x) => x.key === key);
  if (!p) return { from: "", to: "" };
  const [a, b] = p.range(today);
  return { from: isoDate(a), to: isoDate(b) };
}

/** Which preset, if any, the given range is — so the control can say "This month". */
export function matchingPreset({ from, to }, today = new Date()) {
  if (!from || !to) return null;
  return DATE_PRESETS.find((p) => {
    const r = presetRange(p.key, today);
    return r.from === from && r.to === to;
  }) || null;
}
